import { SearchIcon } from '@blocksuite/icons/rc';
import { cssVarV2 } from '@toeverything/theme/v2';
import { type KeyboardEvent, useCallback, useState } from 'react';

import { useI18n } from '@affine/i18n';

import { IconButton } from '../../../button';
import Input from '../../../input';
import { Menu } from '../../../menu';
import { formatPickerAccessibleName } from '../accessible-name';
import * as pickerStyles from '../picker.css';
// import { emojiGroupList } from './gen-data';
import * as styles from './emoji-picker.css';
import { EmojiGroups } from './groups';
import { useRecentEmojis } from './recent';

const skinList = [
  { unicode: '👋', value: undefined },
  { unicode: '👋🏻', value: 0 },
  { unicode: '👋🏼', value: 1 },
  { unicode: '👋🏽', value: 2 },
  { unicode: '👋🏾', value: 3 },
  { unicode: '👋🏿', value: 4 },
];

export const EmojiPicker = ({
  onSelect,
}: {
  onSelect?: (emoji: string) => void;
}) => {
  const t = useI18n();
  const [keyword, setKeyword] = useState<string>('');
  const skinToneLabels = [
    t['com.affine.icon-picker.skin-tone.default'](),
    t['com.affine.icon-picker.skin-tone.0'](),
    t['com.affine.icon-picker.skin-tone.1'](),
    t['com.affine.icon-picker.skin-tone.2'](),
    t['com.affine.icon-picker.skin-tone.3'](),
    t['com.affine.icon-picker.skin-tone.4'](),
  ];

  const [skin, setSkin] = useState<number | undefined>(undefined);
  const { add: addRecent, recentEmojis } = useRecentEmojis();

  const handleEmojiSelect = useCallback(
    (emoji: string) => {
      addRecent(emoji);
      onSelect?.(emoji);
    },
    [addRecent, onSelect]
  );

  const handleSearchKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      e.stopPropagation();
    },
    []
  );

  return (
    <div className={pickerStyles.root}>
      <header className={pickerStyles.searchContainer}>
        <Input
          value={keyword}
          onChange={setKeyword}
          onKeyDown={handleSearchKeyDown}
          className={pickerStyles.searchInput}
          preFix={
            <div style={{ marginLeft: 10, lineHeight: 0 }}>
              <SearchIcon
                style={{ color: cssVarV2.icon.primary, fontSize: 16 }}
              />
            </div>
          }
          aria-label={t['com.affine.icon-picker.search-emojis']()}
          placeholder={t['com.affine.icon-picker.filter']()}
        />
        <Menu
          contentOptions={{
            align: 'center',
            side: 'bottom',
            sideOffset: 4,
            style: { minWidth: 140 },
          }}
          items={
            <ul className={styles.skinList}>
              {skinList.map((skin, index) => (
                <IconButton
                  key={skin.unicode}
                  className={styles.skinItem}
                  style={{ padding: 3 }}
                  size={18}
                  icon={<span>{skin.unicode}</span>}
                  aria-label={formatPickerAccessibleName(
                    skinToneLabels[index],
                    skin.unicode
                  )}
                  tooltip={formatPickerAccessibleName(
                    skinToneLabels[index],
                    skin.unicode
                  )}
                  onClick={() => setSkin(skin.value)}
                />
              ))}
            </ul>
          }
        >
          <IconButton
            size={18}
            style={{
              width: 32,
              height: 32,
              border: `1px solid ${cssVarV2.layer.insideBorder.border}`,
            }}
            aria-label={formatPickerAccessibleName(
              t['com.affine.icon-picker.select-skin-tone'](),
              skin !== undefined
                ? skinList[skin + 1].unicode
                : skinList[0].unicode
            )}
            tooltip={formatPickerAccessibleName(
              t['com.affine.icon-picker.select-skin-tone'](),
              skin !== undefined
                ? skinList[skin + 1].unicode
                : skinList[0].unicode
            )}
            icon={
              <span>
                {skin !== undefined
                  ? skinList[skin + 1].unicode
                  : skinList[0].unicode}
              </span>
            }
          />
        </Menu>
      </header>

      {/* Groups */}
      <EmojiGroups
        recent={recentEmojis}
        onSelect={handleEmojiSelect}
        keyword={keyword}
        skin={skin}
      />
    </div>
  );
};
